import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Eye } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { cn } from '@/lib/cn';
import type { UserRole } from '@/types';

/**
 * Phase-1 helper: lets you flip between mock identities to preview
 * different role-gated experiences. Hidden in production builds.
 */
const useMockAuth = (import.meta.env.VITE_USE_MOCK_AUTH ?? 'true') === 'true';

const roles: Array<{ value: UserRole; label: string; goto: string }> = [
  { value: 'doctor', label: 'Doctor', goto: '/portal/dashboard' },
  { value: 'recruiter', label: 'Recruiter', goto: '/admin/dashboard' },
  { value: 'compliance_admin', label: 'Compliance Admin', goto: '/admin/compliance' },
  { value: 'admin', label: 'Admin', goto: '/admin/dashboard' },
  { value: 'super_admin', label: 'Super Admin', goto: '/admin/team' },
];

export function RoleSwitcher() {
  const { user, switchToRole } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  if (!useMockAuth) return null;

  const current = roles.find((r) => r.value === user?.role);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent-soft px-3 py-1 text-xs font-semibold text-accent-hover transition hover:bg-accent/20',
        )}
        title="Mock-mode role switcher"
      >
        <Eye className="h-3 w-3" />
        Preview as: {current?.label ?? 'Guest'}
        <ChevronDown className={cn('h-3 w-3 transition', open && 'rotate-180')} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-56 rounded-xl border border-border bg-surface p-1 shadow-lift z-50"
          onMouseLeave={() => setOpen(false)}
        >
          <p className="px-2 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
            Switch identity (mock)
          </p>
          {roles.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => {
                switchToRole(r.value);
                setOpen(false);
                navigate(r.goto);
              }}
              className={cn(
                'flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-sm transition hover:bg-muted',
                user?.role === r.value && 'bg-primary-soft text-primary',
              )}
            >
              <span>{r.label}</span>
              <span className="text-[10px] text-ink-subtle">{r.goto}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
